import axios from 'axios'
import { API_BASE_URL, API_TIMEOUT, API_KEY } from '@/config'

function createApiClient() {
    const headers = {
        'Content-Type': 'application/json',
    }
    if (API_KEY) {
        headers['X-API-Key'] = API_KEY
    }

    return axios.create({
        baseURL: API_BASE_URL,
        timeout: API_TIMEOUT,
        headers,
    })
}

const authApi = createApiClient()

// Token管理工具
export class TokenManager {
    static ACCESS_TOKEN_KEY = 'access_token'
    static REFRESH_TOKEN_KEY = 'refresh_token'
    static USER_KEY = 'user_info'

    static getAccessToken() {
        return localStorage.getItem(this.ACCESS_TOKEN_KEY)
    }

    static getRefreshToken() {
        return localStorage.getItem(this.REFRESH_TOKEN_KEY)
    }

    static getUserInfo() {
        const userStr = localStorage.getItem(this.USER_KEY)
        return userStr ? JSON.parse(userStr) : null
    }

    static setTokens(authResponse) {
        localStorage.setItem(this.ACCESS_TOKEN_KEY, authResponse.access_token)
        localStorage.setItem(this.REFRESH_TOKEN_KEY, authResponse.refresh_token)
        localStorage.setItem(this.USER_KEY, JSON.stringify(authResponse.user))
    }

    static clearTokens() {
        localStorage.removeItem(this.ACCESS_TOKEN_KEY)
        localStorage.removeItem(this.REFRESH_TOKEN_KEY)
        localStorage.removeItem(this.USER_KEY)
    }

    static isTokenExpired(token) {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]))
            const currentTime = Math.floor(Date.now() / 1000)
            return payload.exp < currentTime
        } catch {
            return true
        }
    }
}

// 请求拦截器 - 自动添加认证头
authApi.interceptors.request.use(
    (config) => {
        const token = TokenManager.getAccessToken()
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    (error) => Promise.reject(error)
)

// 响应拦截器 - 处理token过期
authApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true

            try {
                const refreshToken = TokenManager.getRefreshToken()
                if (refreshToken) {
                    const newTokens = await refreshAccessToken(refreshToken)
                    TokenManager.setTokens(newTokens)

                    // 重试原始请求
                    originalRequest.headers.Authorization = `Bearer ${newTokens.access_token}`
                    return authApi(originalRequest)
                }
            } catch (refreshError) {
                // 刷新失败，清除所有token并跳转到登录
                TokenManager.clearTokens()
                window.location.href = '/'
            }
        }

        return Promise.reject(error)
    }
)

// ===== 认证API函数 =====

/**
 * 用户注册
 */
export const register = async (data) => {
    try {
        const response = await authApi.post('/auth/register', data)
        return response.data
    } catch (error) {
        console.error('Register error:', error.response?.data)
        const errorMessage = error.response?.data?.detail ||
            error.response?.data?.message ||
            error.response?.data?.msg ||
            error.message ||
            '注册失败'
        throw new Error(errorMessage)
    }
}

/**
 * 用户登录
 */
export const login = async (data) => {
    try {
        const response = await authApi.post('/auth/login', data)
        return response.data
    } catch (error) {
        console.error('Login error:', error.response?.data)
        const errorMessage = error.response?.data?.detail ||
            error.response?.data?.message ||
            error.response?.data?.msg ||
            error.message ||
            '登录失败'
        throw new Error(errorMessage)
    }
}

/**
 * 刷新访问令牌
 */
export const refreshAccessToken = async (refreshToken) => {
    try {
        const response = await authApi.post('/auth/refresh', {
            refresh_token: refreshToken
        })
        return response.data
    } catch (error) {
        throw new Error(error.response?.data?.detail || '刷新令牌失败')
    }
}

/**
 * 获取当前用户信息
 */
export const getCurrentUser = async () => {
    try {
        const response = await authApi.get('/auth/me')
        return response.data
    } catch (error) {
        throw new Error(error.response?.data?.detail || '获取用户信息失败')
    }
}

/**
 * 获取指定货币余额（默认为G币）
 */
export const getCurrencyBalance = async (currency = 'G') => {
    try {
        const response = await authApi.get(`/auth/balance?currency=${currency}`)
        // API 返回的数据结构: { code, msg, data: { balance, ... } }
        const balanceData = response.data.data || response.data
        return {
            currency,
            balance: balanceData.balance || 0,
            symbol: currency,
            name: getCurrencyName(currency)
        }
    } catch (error) {
        console.error('Get currency balance error:', error.response?.data)
        const errorMessage = error.response?.data?.detail ||
            error.response?.data?.message ||
            error.response?.data?.msg ||
            error.message ||
            '获取余额失败'
        throw new Error(errorMessage)
    }
}

/**
 * 获取所有货币余额
 */
export const getAllBalances = async () => {
    try {
        const response = await authApi.get('/auth/balances')
        const balancesData = response.data

        // 转换为统一格式
        return Object.entries(balancesData).map(([currency, balance]) => ({
            currency,
            balance,
            symbol: currency,
            name: getCurrencyName(currency)
        }))
    } catch (error) {
        console.error('Get all balances error:', error.response?.data)
        const errorMessage = error.response?.data?.detail ||
            error.response?.data?.message ||
            error.response?.data?.msg ||
            error.message ||
            '获取余额失败'
        throw new Error(errorMessage)
    }
}

/**
 * 获取货币名称
 */
const getCurrencyName = (currency) => {
    const currencyNames = {
        'G': 'G币',
        'BTC': 'Bitcoin',
        'ETH': 'Ethereum',
        'USDT': 'Tether',
        'USDC': 'USD Coin',
        'BNB': 'Binance Coin'
    }
    return currencyNames[currency] || currency
}

/**
 * 登出（可选实现，主要是清除本地token）
 */
export const logout = async () => {
    TokenManager.clearTokens()
}

// 创建带认证的API实例供其他模块使用
export const createAuthenticatedApi = () => {
    const api = createApiClient()

    // 自动添加认证头
    api.interceptors.request.use(
        (config) => {
            const token = TokenManager.getAccessToken()
            if (token) {
                config.headers.Authorization = `Bearer ${token}`
            }
            return config
        },
        (error) => Promise.reject(error)
    )

    // 处理认证错误
    api.interceptors.response.use(
        (response) => response.data,
        async (error) => {
            if (error.response?.status === 401) {
                // Token过期，尝试刷新
                const refreshToken = TokenManager.getRefreshToken()
                if (refreshToken) {
                    try {
                        const newTokens = await refreshAccessToken(refreshToken)
                        TokenManager.setTokens(newTokens)

                        // 重试原始请求
                        error.config.headers.Authorization = `Bearer ${newTokens.access_token}`
                        return api(error.config)
                    } catch {
                        TokenManager.clearTokens()
                        window.location.href = '/'
                    }
                }
            }
            return Promise.reject(error)
        }
    )

    return api
}

export default authApi
